import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Popup, Button, CheckBox, NumberBox, ScrollView, SelectBox, Toolbar } from "devextreme-react";
import {
    DataGrid,
    Column,
    Editing,
    Scrolling,
    Paging,
    Item,
    Toolbar as GridToolbar,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { LoadPanel } from "devextreme-react/load-panel";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import SplitOrderTargetPopup from "./SplitOrderTargetPopup";
import SplitOrderSerialNumberSelectionPopup from "./SplitOrderSerialNumberSelectionPopup";
import GridOptionsButton from "../../salesOrderManagement/common/GridOptionsButton";
import SalesOrderDataEntryList from "../List";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { alert, confirm } from "devextreme/ui/dialog";
import { loadLookupFromStaticDataSource } from "../../../../../common/Utils";
import { SplitOrderMode, OrderStages, DetailTypes, SplitOrderOperations } from "../../../../../enum/Enum";
import { MESSAGES } from "../../../../../common/Messages";
import routes from "../../../../../assets/apiRoutes.json";
import { HEADER_ORIGINAL_SCHEMA, HEADER_SPLIT_SCHEMA, DETAIL_UDF_ORIGINAL_SCHEMA, DETAIL_UDF_SPLIT_SCHEMA } from "./Layout";
const emptyGuid = "00000000-0000-0000-0000-000000000000";
const baseUrl = () => window._env_?.REACT_APP_APIS || "";

const isNewRow = (row) => (row?.rowId ?? row?.RowId ?? emptyGuid) === emptyGuid;

// Find Transaction list selection mode (whether the picked order is the split source or the
// existing target to merge into).
const LIST_MODE = Object.freeze({ Original: "original", Target: "target" });

// Stages whose inventory is committed (Assigned / Picked-Staged / Shipped-Completed); the source
// quantity that drives the Original <-> Split/Now split comes from Shipped rather than Ordered.
const isShipStage = (stage) =>
    [OrderStages.Assigned, OrderStages.PickedStaged, OrderStages.ShippedCompleted].includes(stage);

const STAGE_OPTIONS = [
    { id: "L", name: "Canceled - No Inventory Effect" },
    { id: "Q", name: "Quote - No Inventory Effect" },
    { id: "P", name: "Plan - No Inventory Effect" },
    { id: "F", name: "Future - No Effect On Net Available" },
    { id: "N", name: "Pending - Effect Net Available" },
    { id: "A", name: "Assigned - Effect Available" },
    { id: "S", name: "Picked/Staged - Effect Available" },
    { id: "C", name: "Shipped/Completed - Effect Available" },
];

// Detail-type codes shared with the other OM detail grids (Delphi DETAILTYPE): the stored
// value is the single-letter code; the grid displays the friendly label via a column lookup.
const DETAIL_TYPE_OPTIONS = [
    { ID: "I", Text: "Inventory" },
    { ID: "N", Text: "Non Inventory" },
];

// Delphi uOMD0018/uOMD0040 pmOptions, minus Sort Records (that one lives on the Sales Order detail grid).
const OPTIONS_MENU_ITEMS = [SplitOrderOperations.SelectSerialNumberRecords];
const NO_OPTIONS_MENU_ITEMS = [];


function mapHeaderToForm(h) {
    if (!h) return {};
    return {
        TransactionNumber: h.transactionNumber ?? h.TransactionNumber,
        TransactionNumberNew: h.transactionNumberNew ?? h.TransactionNumberNew,
        ShippingDate: h.shippingDate ?? h.ShippingDate,
        ShippingDateNew: h.shippingDateNew ?? h.ShippingDateNew,
        Stage: h.stage ?? h.Stage,
        StageNew: h.stageNew ?? h.StageNew,
        TransactionDate: h.transactionDate ?? h.TransactionDate,
        TransactionDateNew: h.transactionDateNew ?? h.TransactionDateNew,
        Reference: h.reference ?? h.Reference,
        ReferenceNew: h.referenceNew ?? h.ReferenceNew,
        Comment: h.comment ?? h.Comment,
        CommentNew: h.commentNew ?? h.CommentNew,
        CustomerIdentityId: h.customerIdentityId ?? h.CustomerIdentityId,
        CustomerIdentityIdNew: h.customerIdentityIdNew ?? h.CustomerIdentityIdNew,
        CustomerName: h.customerName ?? h.CustomerName,
        CustomerNameNew: h.customerNameNew ?? h.CustomerNameNew,
        ShipToCode: h.shipToCode ?? h.ShipToCode,
        ShipToCodeNew: h.shipToCodeNew ?? h.ShipToCodeNew,
        ShipToName: h.shipToName ?? h.ShipToName,
        ShipToNameNew: h.shipToNameNew ?? h.ShipToNameNew,
        RCustomer: h.rCustomer ?? h.RCustomer,
        RCustomerNew: h.rCustomerNew ?? h.RCustomerNew,
        RShipper: h.rShipper ?? h.RShipper,
        RShipperNew: h.rShipperNew ?? h.RShipperNew,
        FieldsReadOnlyWhenInsertToExisting: h.fieldsReadOnlyWhenInsertToExisting ?? h.FieldsReadOnlyWhenInsertToExisting,
        PriceScheduleRowId: h.priceScheduleRowId ?? h.PriceScheduleRowId,
        PriceScheduleApplyMethod: h.priceScheduleApplyMethod ?? h.PriceScheduleApplyMethod,
    };
}

function mapDetailToForm(d) {
    if (!d) return {};
    return {
        ReferenceNumber: d.referenceNumber ?? d.ReferenceNumber,
        ReferenceNumberNew: d.referenceNumberNew ?? d.ReferenceNumberNew,
        UserDefinedCode1: d.userDefinedCode1 ?? d.UserDefinedCode1,
        UserDefinedCode1New: d.userDefinedCode1New ?? d.UserDefinedCode1New,
        UserDefinedQuantity1: d.userDefinedQuantity1 ?? d.UserDefinedQuantity1,
        UserDefinedQuantity1New: d.userDefinedQuantity1New ?? d.UserDefinedQuantity1New,
        UserDefinedDate1: d.userDefinedDate1 ?? d.UserDefinedDate1,
        UserDefinedDate1New: d.userDefinedDate1New ?? d.UserDefinedDate1New,
        UserDefinedReference1: d.userDefinedReference1 ?? d.UserDefinedReference1,
        UserDefinedReference1New: d.userDefinedReference1New ?? d.UserDefinedReference1New,
        UserDefinedComment: d.userDefinedComment ?? d.UserDefinedComment,
        UserDefinedCommentNew: d.userDefinedCommentNew ?? d.UserDefinedCommentNew,
        Comment: d.comment ?? d.Comment,
        CommentNew: d.commentNew ?? d.CommentNew,
        ItemDesc1: d.itemDesc1 ?? d.ItemDesc1,
        ItemDesc2: d.itemDesc2 ?? d.ItemDesc2,
        ItemAvailable: d.itemAvailable ?? d.ItemAvailable,
        LotDesc1: d.lotDesc1 ?? d.LotDesc1,
        LotDesc2: d.lotDesc2 ?? d.LotDesc2,
        LotAvailable: d.lotAvailable ?? d.LotAvailable,
    };
}

function SplitOrderPopup(props) {
    var currentUser = props.oidc.user;
    const { showPopup = {}, setShowPopup, setLoadPanelVisible, onSuccess, oidc } = props;
    const visible = showPopup.visible === true;
    const sourceRProfileFromForm = showPopup.sourceRProfile ?? null;
    const sourceWarehouseFromForm = showPopup.sourceWarehouse ?? null;
    // Delphi OMD0040/OMD0018 SplitMode: 0 = "Split Order" (source is the current order, no Find
    // Transaction), 1 = "Split Order by Transaction" (find the source order first). Defaults to 1
    // so the existing "By Transaction - Advanced" entry point is unchanged.
    const splitMode = showPopup.splitMode ?? SplitOrderMode.ByTransaction;
    // "Advanced" adds the Split Method dialog (split to a new OR an existing order). The basic
    // Split Order / Split Order by Transaction operations always split to a new order.
    const advanced = showPopup.advanced === true;
    const sourceHeaderRowId = showPopup.headerRowId ?? null;
    const accessToken = oidc?.user?.access_token;

    const [insertToRowId, setInsertToRowId] = useState(null);
    const [originalHeaderRowId, setOriginalHeaderRowId] = useState(null);
    const [listMode, setListMode] = useState(LIST_MODE.Original);
    const [showTargetPopup, setShowTargetPopup] = useState(false);
    const [showSerialNumberSelection, setShowSerialNumberSelection] = useState({ visible: false, headerRowId: null });
    const [showListPopup, setShowListPopup] = useState(false);
    const [splitTargetResolved, setSplitTargetResolved] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedHeader, setSelectedHeader] = useState(null);
    const [details, setDetails] = useState([]);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [headerOriginal, setHeaderOriginal] = useState({});
    const [headerSplit, setHeaderSplit] = useState({});
    const [detailOriginal, setDetailOriginal] = useState({});
    const [detailSplit, setDetailSplit] = useState({});
    const [totals, setTotals] = useState({ originalOrder: 0, remaining: 0, balance: 0 });
    const [openSplitOrder, setOpenSplitOrder] = useState(true);
    const [qtyFieldName, setQtyFieldName] = useState("QuantityOrdered");
    const [rawHeader, setRawHeader] = useState(null);
    const [sourceRProfile, setSourceRProfile] = useState(null);

    const headerOrigRefs = useRef({});
    const headerSplitRefs = useRef({});
    const detailOrigRefs = useRef({});
    const detailSplitRefs = useRef({});
    const findTransactionCallbackRef = useRef(null);
    const listSelectionModeRef = useRef(LIST_MODE.Original);
    // True while the Split Method popup was opened by the Split button (basic flow) so its OK
    // runs the split instead of reloading the dialog.
    const splitAfterTargetRef = useRef(false);
    /* Guards for the serial-hold release below: it must not run after a split that succeeded
       (that split consumed the holds), and it must run at most once per close - Cancel sets
       visible=false, which makes DevExtreme fire onHiding and call closePopup a second time. */
    const splitCompletedRef = useRef(false);
    const serialHoldsReleasedRef = useRef(false);
    // Holds the item picked in the in-cell item lookup until setCellValue runs.
    const selectedItemRef = useRef(null);
    // Generates unique keys for blank "add new item" rows.
    const newRowSeq = useRef(0);

    // Builds a blank "add new item" row (advanced flow only; basic split modes only split
    // lines already on the source order).
    const makeBlankRow = () => {
        newRowSeq.current += 1;
        return { _key: `new-${newRowSeq.current}`, rowId: emptyGuid, RowId: emptyGuid, detailType: DetailTypes.Item };
    };

    const recalcTotals = useCallback((rows, stage) => {
        const isShip = isShipStage(stage);
        const original = rows
            .filter((r) => (r.rowId ?? r.RowId) !== emptyGuid)
            .reduce((sum, r) => sum + (isShip ? (r.quantityShipped ?? r.QuantityShipped ?? 0) : (r.quantityOrdered ?? r.QuantityOrdered ?? 0)), 0);
        const remaining = rows
            .filter((r) => (r.rowId ?? r.RowId) !== emptyGuid)
            .reduce((sum, r) => sum + (r.remainingQty ?? r.RemainingQty ?? 0), 0);
        setTotals({ originalOrder: original, remaining, balance: original - remaining });
    }, []);

    const loadData = useCallback(async (srcHeaderRowId, targetInsertId) => {
        if (!visible || !srcHeaderRowId || srcHeaderRowId === emptyGuid) return;
        setLoading(true);
        setLoadPanelVisible(true);
        setDataLoaded(false);
        try {
            const res = await axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetSplitOrderAdvancedData`, {
                params: { headerRowId: srcHeaderRowId, insertToRowId: targetInsertId || undefined },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = res.data ?? {};
            const h = data.header ?? data.Header ?? {};
            const mappedRows = (data.details ?? data.Details ?? []).map((d, i) => ({
                ...d,
                _key: d.rowId ?? d.RowId ?? `row-${i}`,
            }));
            // Basic "Split Order" / "Split Order by Transaction" (Delphi OMD0018) show the source
            // order's existing detail lines so the user enters a Split/Now quantity per line; the
            // backend decrements the source. The advanced flow (OMD0040) instead starts empty and
            // the user adds the items to move onto a new/existing order.
            let rows = advanced ? mappedRows.filter(isNewRow) : mappedRows.filter((r) => !isNewRow(r));
            // Advanced adds arbitrary items, so guarantee a trailing blank "add new item" row.
            if (advanced && !rows.some(isNewRow)) {
                rows = [...rows, makeBlankRow()];
            }
            setRawHeader(h);
            setSourceRProfile(h.rProfile ?? h.RProfile ?? sourceRProfileFromForm);
            setHeaderOriginal(mapHeaderToForm(h));
            setHeaderSplit(mapHeaderToForm(h));
            setDetails(rows);
            setQtyFieldName(data.qtyFieldName ?? data.QtyFieldName ?? "QuantityOrdered");
            recalcTotals(rows, h.stage ?? h.Stage);
            setSelectedDetail(rows[0] ?? null);
            setDetailOriginal(rows[0] ? mapDetailToForm(rows[0]) : {});
            setDetailSplit(rows[0] ? mapDetailToForm(rows[0]) : {});
            setInsertToRowId(targetInsertId || null);
            setDataLoaded(true);
        } catch (err) {
            notify({ message: err?.response?.data?.message || err?.message || MESSAGES.ERROR, width: 320 }, "error", 5000);
        } finally {
            setLoading(false);
            setLoadPanelVisible(false);
        }
    }, [visible, advanced, sourceRProfileFromForm, accessToken, setLoadPanelVisible, recalcTotals]);

    const pickerFilter = useMemo(() => {
        if (listMode === LIST_MODE.Target) {
            const reviewStage = [["REVIEWSTAGE", "=", null], "or", ["REVIEWSTAGE", "=", "Review Pending"]];
            const conditions = [["RECORDTYPE", "=", "R"], reviewStage];
            if (sourceRProfile) conditions.push(["R_PROFILE", "=", sourceRProfile]);
            return conditions.reduce((acc, c, i) => (i === 0 ? [c] : [...acc, "and", c]), []);
        }
        return [
            ["StageFlag", "=", "N"],
            "and",
            [["INVENTORYREVIEW", "=", null], "or", ["INVENTORYREVIEW", "=", "N"]],
        ];
    }, [listMode, sourceRProfile]);


    const handleListRecordSelected = useCallback(async (row) => {
        const rowId = row && (row.ROWID ?? row.RowId);
        if (!rowId || rowId === emptyGuid) return;
        setShowListPopup(false);

        if (listSelectionModeRef.current === LIST_MODE.Target) {
            setSelectedHeader(row);
            setShowTargetPopup(true);
            return;
        }

        setSelectedHeader(null);
        setOriginalHeaderRowId(rowId);
        await loadData(rowId, null);
        // Only the advanced flow offers the Split Method (new/existing) dialog; the basic
        // "Split Order by Transaction" splits straight to a new order.
        if (advanced) setShowTargetPopup(true);
    }, [loadData, advanced]);

    const closeListPopup = useCallback(() => {
        findTransactionCallbackRef.current = null;
        setShowListPopup(false);
    }, []);

    const onFindPopupOpen = () => {
        listSelectionModeRef.current = LIST_MODE.Original;
        setListMode(LIST_MODE.Original);
        setShowListPopup(true);
        setOriginalHeaderRowId(null);
    };

    useEffect(() => {
        if (!visible) {
            setInsertToRowId(null);
            setOriginalHeaderRowId(null);
            setShowTargetPopup(false);
            setShowListPopup(false);
            setShowSerialNumberSelection({ visible: false, headerRowId: null });
            splitAfterTargetRef.current = false;
            splitCompletedRef.current = false;
            serialHoldsReleasedRef.current = false;
            findTransactionCallbackRef.current = null;
            listSelectionModeRef.current = LIST_MODE.Original;
            setListMode(LIST_MODE.Original);
            setSplitTargetResolved(false);
            setDataLoaded(false);
            setLoading(false);
            setSourceRProfile(null);
            setSelectedHeader(null);
            setRawHeader(null);
            setDetails([]);
            return;
        }
        setSourceRProfile(sourceRProfileFromForm);
        setSelectedHeader(null);
        setInsertToRowId(null);
        setSplitTargetResolved(false);
        setDataLoaded(false);
        setShowTargetPopup(false);
        // Clear anything left over from a previous open (e.g. "Split Order" auto-loaded the current
        // order). "Split Order by Transaction" must start empty until a source order is found.
        setDetails([]);
        setSelectedDetail(null);
        setHeaderOriginal({});
        setHeaderSplit({});
        setDetailOriginal({});
        setDetailSplit({});
        setRawHeader(null);
        setTotals({ originalOrder: 0, remaining: 0, balance: 0 });
        setQtyFieldName("QuantityOrdered");
        setOpenSplitOrder(true);
        // Delphi DisplaySplitOrderDialog(0): "Split Order" mode fixes the source to the current
        // order and loads its details straight into the grid — no Find Transaction and no Split
        // Method (target) dialog; the split always creates a new order.
        if (splitMode === SplitOrderMode.CurrentOrder && sourceHeaderRowId && sourceHeaderRowId !== emptyGuid) {
            setLoading(true);
            setOriginalHeaderRowId(sourceHeaderRowId);
            loadData(sourceHeaderRowId, null);
        } else {
            setOriginalHeaderRowId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    // Plain function (not memoized) so it always sees the latest runSplit closure.
    const handleTargetConfirm = (selectedInsertToRowId) => {
        setShowTargetPopup(false);
        const target = selectedInsertToRowId || null;
        // Basic flow: the target was requested from the Split button, so run the split now.
        if (splitAfterTargetRef.current) {
            splitAfterTargetRef.current = false;
            setInsertToRowId(target);
            runSplit(target);
            return;
        }
        // Advanced flow: reload the dialog against the chosen target for review before Split.
        setSplitTargetResolved(true);
        loadData(originalHeaderRowId, target);
    };

    const handleTargetCancel = useCallback(() => {
        splitAfterTargetRef.current = false;
        setShowTargetPopup(false);
    }, []);

    useEffect(() => {
        if (!visible) return;
        loadLookupFromStaticDataSource("Stage", STAGE_OPTIONS, headerOriginal, headerOrigRefs);
        loadLookupFromStaticDataSource("StageNew", STAGE_OPTIONS, headerSplit, headerSplitRefs);
    }, [visible, headerOriginal, headerSplit]);

    /* Delphi uOMD0018.btnCancelClick (uOMD0018.pas:482-495) runs SQLSTR_CANCELSERIALNUMBERSPLIT on the
       way out: every serial the order is still holding at STATUS 'S' goes back to 'A', so a selection the
       user walked away from is not silently carried into the next split of this order. Cancel / X / Esc
       only - a split that succeeded keeps the holds it just consumed. Fire and forget: the dialog must
       close either way. */
    const releaseSerialHolds = useCallback(() => {
        if (splitCompletedRef.current || serialHoldsReleasedRef.current) return;
        if (!originalHeaderRowId || !accessToken) return;
        serialHoldsReleasedRef.current = true;
        axios
            .post(`${baseUrl()}/${routes.OM.CancelSplitOrderSerialNumbers}`, null, {
                params: { headerRowId: originalHeaderRowId },
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .catch((err) => {
                notify(
                    { message: err?.response?.data?.message || err?.message || MESSAGES.ERROR, width: 320 },
                    "error",
                    5000
                );
            });
    }, [originalHeaderRowId, accessToken]);

    const closePopup = useCallback(() => {
        releaseSerialHolds();
        setShowPopup({ visible: false });
    }, [releaseSerialHolds, setShowPopup]);

    // Delphi pmOptionsPopup: opSelectSerialNumberRecords is shown only while the loaded order has
    // serial numbers shipped against it (header query SERIALNUMBERCOUNT > 0). It is the only item on
    // the menu, so with no serials there is nothing to open and the button itself goes disabled.
    const optionsMenuItems = useMemo(() => {
        const serialNumberCount = rawHeader?.serialNumberCount ?? rawHeader?.SerialNumberCount ?? 0;
        return serialNumberCount > 0 ? OPTIONS_MENU_ITEMS : NO_OPTIONS_MENU_ITEMS;
    }, [rawHeader]);

    const onOptionsItemClick = useCallback(
        (e) => {
            if (e.itemData?.id !== SplitOrderOperations.SelectSerialNumberRecords.id) return;
            setShowSerialNumberSelection({ visible: true, headerRowId: originalHeaderRowId });
        },
        [originalHeaderRowId]
    );

    const onDetailRowClick = (e) => {
        const row = e.data;
        setSelectedDetail(row);
        setDetailOriginal(mapDetailToForm(row));
        setDetailSplit(mapDetailToForm(row));
    };

   const itemsStore = useMemo(() => {
        const rWarehouse =
            rawHeader?.rPrimaryDefaultWh ?? rawHeader?.RPrimaryDefaultWh ??
            sourceWarehouseFromForm ??
            rawHeader?.rFmShipper ?? rawHeader?.RFmShipper;
        if (!rWarehouse || rWarehouse === emptyGuid) return [];
        const psId = rawHeader?.priceScheduleRowId ?? rawHeader?.PriceScheduleRowId;
        return {
            store: createStore({
                loadUrl: `${baseUrl()}/${routes.OM.GetItemsWithServerSidePaging}`,
                key: "rowid",
                loadParams: {
                    RWarehouse: rWarehouse,
                    RPriceScheduleHeader: psId && psId !== emptyGuid ? psId : null,
                    ApplyMethod: rawHeader?.priceScheduleApplyMethod ?? rawHeader?.PriceScheduleApplyMethod ?? null,
                    AllowUnSpecified: rawHeader?.priceScheduleAllowUnspecified ?? rawHeader?.PriceScheduleAllowUnspecified ?? null,
                },
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = { Authorization: `Bearer ${accessToken}` };
                },
            }),
            paginate: true,
            pageSize: 100,
        };
    }, [rawHeader, sourceWarehouseFromForm, accessToken]);

    // In-cell item picker (rendered only for the blank "new item" rows).
    const renderItemCell = useCallback((cell) => (
        <SelectBox
            dataSource={itemsStore}
            valueExpr="rowid"
            displayExpr="code"
            searchEnabled
            searchExpr="code"
            searchMode="contains"
            searchTimeout={500}
            showDataBeforeSearch
            defaultValue={cell.row.data?.rItem ?? cell.row.data?.RItem}
            onValueChanged={(e) => {
                selectedItemRef.current = { rowid: e.value, code: e.component.option("displayValue") };
                cell.setValue(e.component.option("displayValue"));
            }}
        />
    ), [itemsStore]);

    const setItemCellValue = useCallback((newData, value) => {
        newData.itemCode = value;
        newData.rItem = selectedItemRef.current?.rowid ?? null;
        selectedItemRef.current = null;
    }, []);

    // Item picker is only allowed on the blank "new item" rows; original lines are read-only.
    const onEditorPreparing = useCallback((e) => {
        if (e.parentType !== "dataRow") return;
        if (e.dataField === "itemCode" && !isNewRow(e.row?.data)) {
            e.cancel = true;
        }
    }, []);

    const applyGridEdit = (rowKey, changed) => {
        setDetails((prev) => {
            const stage = headerSplit.StageNew ?? headerSplit.Stage;
            const isShip = isShipStage(stage);
            const next = prev.map((row) => {
                if ((row._key ?? row.rowId) !== rowKey) return row;
                const merged = { ...row, ...changed };
                const newItemRow = isNewRow(merged);
                const sourceQty = isShip
                    ? (merged.quantityShipped ?? merged.QuantityShipped ?? 0)
                    : (merged.quantityOrdered ?? merged.QuantityOrdered ?? 0);
                const rawSplit = merged.remainingQty ?? merged.RemainingQty;
                const splitQty = rawSplit == null || rawSplit === 0 ? null : rawSplit;
                // "Original" column = what stays on the source line = sourceQty - split (full
                // source when nothing is split). New items have no source quantity.
                let quantityShippedNew = newItemRow ? 0 : sourceQty;
                let excessQuantity = 0;
                if (!newItemRow && splitQty != null) {
                    if (sourceQty - splitQty >= 0) quantityShippedNew = sourceQty - splitQty;
                    else { quantityShippedNew = 0; excessQuantity = splitQty - sourceQty; }
                }
                return {
                    ...merged,
                    remainingQty: splitQty,
                    RemainingQty: splitQty,
                    quantityShippedNew,
                    QuantityShippedNew: quantityShippedNew,
                    excessQuantity,
                    ExcessQuantity: excessQuantity,
                };
            });
            // Advanced only: once the trailing blank row gets an item, append a fresh blank row
            // (Delphi OMD0040 adds a new entry row after each item) so more items can be added.
            // Basic split modes never add arbitrary items, so no blank row is appended.
            const hasTrailingBlank = next.some((r) => isNewRow(r) && !(r.rItem ?? r.RItem));
            if (advanced && !hasTrailingBlank) {
                next.push(makeBlankRow());
            }
            recalcTotals(next, stage);
            const sel = next.find((r) => (r._key ?? r.rowId) === rowKey);
            if (sel) {
                setSelectedDetail(sel);
                setDetailOriginal((f) => ({ ...f, ...mapDetailToForm(sel) }));
                setDetailSplit((f) => ({ ...f, ...mapDetailToForm(sel) }));
            }
            return next;
        });
    };

    const handleGridRowUpdated = (e) => {
        applyGridEdit(e.key, e.data || {});
    };

    const handleHeaderSplitChange = (name, value) => {
        setHeaderSplit((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "StageNew") recalcTotals(details, value);
            return next;
        });
    };

    const handleDetailSplitChange = (name, value) => {
        setDetailSplit((prev) => ({ ...prev, [name]: value }));
        if (!selectedDetail) return;
        setDetails((list) =>
            list.map((row) => {
                if ((row._key ?? row.rowId) !== (selectedDetail._key ?? selectedDetail.rowId)) return row;
                const patch = {};
                if (name === "ReferenceNumberNew") patch.referenceNumberNew = value;
                if (name === "UserDefinedCode1New") patch.userDefinedCode1New = value;
                if (name === "UserDefinedQuantity1New") patch.userDefinedQuantity1New = value;
                if (name === "UserDefinedDate1New") patch.userDefinedDate1New = value;
                if (name === "UserDefinedReference1New") patch.userDefinedReference1New = value;
                if (name === "UserDefinedCommentNew") patch.userDefinedCommentNew = value;
                if (name === "CommentNew") patch.commentNew = value;
                return { ...row, ...patch };
            })
        );
    };

    const buildExecutePayload = (insertTo) => ({
        HeaderRowId: originalHeaderRowId,
        InsertToRowId: insertTo || null,
        SplitMode: splitMode,
        OpenSplitOrder: openSplitOrder,
        Header: {
            ...rawHeader,
            TransactionNumberNew: headerSplit.TransactionNumberNew,
            ShippingDateNew: headerSplit.ShippingDateNew,
            StageNew: headerSplit.StageNew,
            TransactionDateNew: headerSplit.TransactionDateNew,
            ReferenceNew: headerSplit.ReferenceNew,
            CommentNew: headerSplit.CommentNew,
            RCustomerNew: headerSplit.RCustomerNew ?? rawHeader?.rCustomerNew,
            RShipperNew: headerSplit.RShipperNew ?? rawHeader?.rShipperNew,
        },
        Details: details.map((d) => ({
            ...d,
            RemainingQty: d.remainingQty ?? d.RemainingQty,
            OverridePrice: d.overridePrice ?? d.OverridePrice,
        })),
    });

    // Posts the split for the resolved target and closes/refreshes on success.
    const runSplit = async (insertTo) => {
        setLoadPanelVisible(true);
        try {
            const res = await axios.post(
                `${baseUrl()}/om/SalesOrderHeaderOperations/ExecuteSplitOrderAdvanced`,
                buildExecutePayload(insertTo),
                { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } }
            );
            const data = res.data ?? {};
            const detailsProcessed = data.detailsProcessed ?? data.DetailsProcessed ?? 0;
            const baseMsg = data.message || MESSAGES.SPLIT_ORDER_COMPLETED;
            const processedMsg = MESSAGES.SPLIT_ORDER_DETAILS_PROCESSED.replace("{0}", detailsProcessed);
            await alert(`${baseMsg}<br/>${processedMsg}`, MESSAGES.SUCCESS);
            // The split consumed the held serials - closing must not release what it moved.
            splitCompletedRef.current = true;
            setShowPopup({ visible: false });
            if (onSuccess) {
                onSuccess({
                    newHeaderRowId: data.newHeaderRowId ?? data.NewHeaderRowId,
                    openSplitOrder,
                });
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || MESSAGES.ERROR;
            await alert(msg, MESSAGES.ERROR);
        } finally {
            setLoadPanelVisible(false);
        }
    };

    const handleSplit = async () => {
        if (!dataLoaded) return;
        const lines = details.filter((d) => (d.remainingQty ?? d.RemainingQty) > 0);
        if (lines.length === 0) {
            await alert(MESSAGES.SPLIT_ORDER_QUANTITY_REQUIRED, MESSAGES.INFORMATION);
            return;
        }
        const newLinesMissingItem = lines.filter((d) => isNewRow(d) && !(d.rItem ?? d.RItem));
        if (newLinesMissingItem.length > 0) {
            await alert(MESSAGES.SPLIT_ORDER_NEW_LINE_ITEM_REQUIRED, MESSAGES.INFORMATION);
            return;
        }
        // Basic Split Order / Split Order by Transaction pick the target (new/existing) when Split
        // is clicked (Delphi OMD0018 btnSplitClick -> GetInsertToTransaction); the Split Method
        // popup's OK then runs the split. Advanced already resolved its target up front.
        if (!advanced) {
            splitAfterTargetRef.current = true;
            setShowTargetPopup(true);
            return;
        }
        const agreed = await confirm(MESSAGES.SPLIT_ORDER_CONFIRM, MESSAGES.CONFIRM);
        if (!agreed) return;
        await runSplit(insertToRowId);
    };

    const fieldsDisabled =
        !dataLoaded || headerSplit.FieldsReadOnlyWhenInsertToExisting === true;
    const appendToExisting = Boolean(insertToRowId && insertToRowId !== emptyGuid);
    const gridEditable = dataLoaded;

    const headerSplitSchema = useMemo(() => {
        if (!fieldsDisabled) return HEADER_SPLIT_SCHEMA;
        return HEADER_SPLIT_SCHEMA.map((g) => ({
            ...g,
            fields: g.fields.map((f) => ({
                ...f,
                readOnly: f.name !== "TransactionNumberNew",
            })),
        }));
    }, [fieldsDisabled]);

    const detailSplitSchema = useMemo(() => {
        if (!fieldsDisabled) return DETAIL_UDF_SPLIT_SCHEMA;
        return DETAIL_UDF_SPLIT_SCHEMA.map((g) => ({
            ...g,
            fields: g.fields.map((f) => ({ ...f, readOnly: true })),
        }));
    }, [fieldsDisabled]);

    const gridColumns = useMemo(
        () => {
            const stage = headerSplit.StageNew ?? headerSplit.Stage;
            const isShip = isShipStage(stage);
            // Source total per line drives the Original <-> Split/Now split (Ordered, or Shipped
            // for shipped stages).
            const sourceQtyOf = (row) => isShip
                ? (row?.quantityShipped ?? row?.QuantityShipped ?? 0)
                : (row?.quantityOrdered ?? row?.QuantityOrdered ?? 0);

            const cols = [
                { dataField: "lineNumber", caption: "Line #", width: 70, allowEditing: false },
                {
                    dataField: "detailType",
                    caption: "Type",
                    width: 55,
                    allowEditing: false,
                    lookup: { dataSource: DETAIL_TYPE_OPTIONS, valueExpr: "ID", displayExpr: "Text" },
                },
                { dataField: "warehouse", caption: "WH", width: 70, allowEditing: false },
                {
                    dataField: "itemCode",
                    caption: "Item",
                    width: 110,
                    // Item-add is an advanced-only (OMD0040) capability; basic split modes only
                    // split lines already on the source order.
                    allowEditing: advanced && gridEditable,
                    editCellRender: renderItemCell,
                    setCellValue: setItemCellValue,
                },
                { dataField: "description", caption: "Description", width: 140, allowEditing: false },
                { dataField: "lotCode", caption: "Lot", width: 80, allowEditing: false },
                { dataField: "holdCode", caption: "Hold Code", width: 80, allowEditing: false },
                { dataField: "quantityOrdered", caption: "Ordered", width: 85, allowEditing: false },
                { dataField: "quantityShipped", caption: "Shipped", width: 85, allowEditing: false },
                { dataField: "quantityBackorder", caption: "Back Order", width: 90, allowEditing: false },
            ];

            // Basic Split Order / Split Order by Transaction show a two-way "Original" (stays on the
            // source) column: Original = source qty - Split/Now. Editing Original moves the balance
            // into Split/Now; editing Split/Now updates Original (Delphi OMD0018 quantity link).
            if (!advanced) {
                cols.push({
                    dataField: "quantityShippedNew",
                    caption: "Original",
                    width: 95,
                    dataType: "number",
                    allowEditing: gridEditable,
                    setCellValue: (newData, value, currentRow) => {
                        newData.quantityShippedNew = value;
                        const remaining = sourceQtyOf(currentRow) - (value ?? 0);
                        newData.remainingQty = remaining > 0 ? remaining : null;
                    },
                });
            }

            cols.push({
                dataField: "remainingQty",
                caption: "Split/Now",
                width: 95,
                dataType: "number",
                allowEditing: gridEditable,
            });

            // OR Price is an advanced-only override; hidden for the basic split operations.
            if (advanced) {
                cols.push({
                    dataField: "overridePrice",
                    caption: "OR Price",
                    width: 90,
                    dataType: "number",
                    allowEditing: gridEditable,
                });
            }

            return cols;
        },
        [gridEditable, advanced, headerSplit.StageNew, headerSplit.Stage, renderItemCell, setItemCellValue]
    );
    const handleFormLoad = useCallback(() => {
        // loadLookupFromStaticData(formRefs, "STATUS", statuses);
 
     }, []);
    return (
        <>
        <Popup
            visible={visible}
            onHiding={closePopup}
            title={advanced ? MESSAGES.SPLIT_ORDER_TITLE_ADVANCED : MESSAGES.SPLIT_ORDER_TITLE}
            width="95%"
            height="90%"
            showCloseButton
            dragEnabled
            resizeEnabled
        >
            <div
                className="split-order-advanced-popup"
                style={{
                    padding: "8px 12px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    boxSizing: "border-box",
                }}
            >
                <LoadPanel
                    visible={loading}
                    shading
                    shadingColor="rgba(0,0,0,0.4)"
                    showIndicator
                    showPane
                    hideOnOutsideClick={false}
                    message="Loading..."
                    container=".split-order-advanced-popup"
                    position={{ my: "center", at: "center", of: ".split-order-advanced-popup" }}
                />
                    {splitMode !== SplitOrderMode.CurrentOrder && (
                       <Toolbar>
                        <Item location="after">
                        <Button
                                text={MESSAGES.FIND_TRANSACTION}
                                type="default"
                                stylingMode="contained"
                                onClick={onFindPopupOpen}
                            />
                        </Item>
                       </Toolbar>
                    )}
                <div style={{ flex: "1 1 0", minHeight: 0, overflow: "auto" }}>
                <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <FormField
                            layoutId="D0923C05-6823-4592-9E43-A53FF57FD6F6"
                            layoutName="Split Order Header Original"
                            defaultFormSchema={HEADER_ORIGINAL_SCHEMA}
                            formFields={headerOriginal}
                            setformFields={setHeaderOriginal}
                            handleFieldValueChange={() => {}}
                            formRefs={headerOrigRefs}
                            onFormLoad ={handleFormLoad}
                            currrentUser={currentUser}
                            hasOnlyOneColumn
                        />
                        <FormField
                            layoutId="075F4D2B-74F1-4069-87F8-385127084C2C"
                            layoutName="Split Order Header Split"
                            defaultFormSchema={headerSplitSchema}
                            formFields={headerSplit}
                            setformFields={setHeaderSplit}
                            handleFieldValueChange={handleHeaderSplitChange}
                            formRefs={headerSplitRefs}
                            onFormLoad ={handleFormLoad}
                            currrentUser={currentUser}
                            hasOnlyOneColumn
                        />
                        <FormField
                            layoutId="A357D9B9-0F19-4B11-A5E7-250B26B6CAA6"
                            layoutName="Split Detail Original"
                            defaultFormSchema={DETAIL_UDF_ORIGINAL_SCHEMA}
                            formFields={detailOriginal}
                            setformFields={setDetailOriginal}
                            handleFieldValueChange={() => {}}
                            formRefs={detailOrigRefs}
                            onFormLoad ={handleFormLoad}
                            currrentUser={currentUser}
                            hasOnlyOneColumn
                        />

                        <FormField
                            layoutId="0FAEA542-FD1F-46E8-835A-F4285B05F3B3"
                            layoutName="Split Detail Split"
                            defaultFormSchema={DETAIL_UDF_SPLIT_SCHEMA}
                            formFields={detailSplit}
                            setformFields={setDetailSplit}
                            handleFieldValueChange={handleDetailSplitChange}
                            formRefs={detailSplitRefs}
                            onFormLoad ={handleFormLoad}
                            currrentUser={currentUser}
                            hasOnlyOneColumn
                        />
                    
                <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200, border: "1px solid #ccc", padding: 8 }}>
                        <strong>Item</strong>
                        <div>Description 1: {selectedDetail?.itemDesc1 ?? selectedDetail?.ItemDesc1 ?? ""}</div>
                        <div>Description 2: {selectedDetail?.itemDesc2 ?? selectedDetail?.ItemDesc2 ?? ""}</div>
                        <div>Available: {selectedDetail?.itemAvailable ?? selectedDetail?.ItemAvailable ?? 0}</div>
                        <strong style={{ display: "block", marginTop: 8 }}>Lot</strong>
                        <div>Description 1: {selectedDetail?.lotDesc1 ?? selectedDetail?.LotDesc1 ?? ""}</div>
                        <div>Description 2: {selectedDetail?.lotDesc2 ?? selectedDetail?.LotDesc2 ?? ""}</div>
                        <div>Available: {selectedDetail?.lotAvailable ?? selectedDetail?.LotAvailable ?? 0}</div>
                        <div>Original Order: {totals.originalOrder?.toFixed?.(2) ?? totals.originalOrder}</div>
                        <div>Remaining: {totals.remaining?.toFixed?.(2) ?? totals.remaining}</div>
                        <div>Balance: {totals.balance?.toFixed?.(2) ?? totals.balance}</div>
                        <CheckBox
                            text="Open Split Order"
                            value={openSplitOrder}
                            onValueChanged={(e) => setOpenSplitOrder(e.value)}
                            style={{ marginTop: 12 }}
                        />
                    </div>
                </div>
                </div>

                <div style={{ marginTop: 8 }}>
                    <DataGrid
                        height="100%"
                        dataSource={details}
                        keyExpr="_key"
                        showBorders
                        hoverStateEnabled
                        onRowClick={onDetailRowClick}
                        onEditorPreparing={onEditorPreparing}
                        onCellPrepared={(e) => {
                            if (e.rowType === "data" && (e.column?.dataField === "remainingQty" || e.column?.dataField === "quantityShippedNew" || e.column?.dataField === "overridePrice")) {
                                e.cellElement.style.backgroundColor = "#f0f8ff";
                            }
                        }}
                        onRowUpdated={handleGridRowUpdated}
                    >
                        <Scrolling mode="virtual" />
                        <Paging enabled={false} />
                        <Editing mode="cell" allowUpdating={gridEditable} />
                            <GridToolbar>
                                <Item location="after">
                                    <GridOptionsButton
                                        items={optionsMenuItems}
                                        onItemClick={onOptionsItemClick}
                                        disabled={optionsMenuItems.length === 0}
                                    />
                                </Item>
                            </GridToolbar>
                        {gridColumns.map((col) => (
                            <Column key={col.dataField} {...col} />
                        ))}
                    </DataGrid>
                </div>
                </>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, flexShrink: 0 }}>
                    <Button text="Split" type="default" onClick={handleSplit} disabled={!gridEditable} />
                    <Button text="Cancel" onClick={closePopup} />
                </div>
            </div>
        </Popup>
        {showSerialNumberSelection.visible && (
            <SplitOrderSerialNumberSelectionPopup
                showPopup={showSerialNumberSelection}
                setShowPopup={setShowSerialNumberSelection}
            />
        )}
        {showTargetPopup && (
            <SplitOrderTargetPopup
                showTargetPopup={showTargetPopup}
                setShowTargetPopup={(value) => setShowTargetPopup(value)}
                onFindTransaction={(value) => {
                    listSelectionModeRef.current = LIST_MODE.Target;
                    setListMode(LIST_MODE.Target);
                    setShowListPopup(value);
                }}
                onConfirm={handleTargetConfirm}
                onCancel={handleTargetCancel}
                selectedHeader={selectedHeader}
            />
        )}
        <Popup
            visible={showListPopup}
            onHiding={closeListPopup}
            title={MESSAGES.FIND_TRANSACTION}
            width="95%"
            height="90%"
            showCloseButton
            dragEnabled
            resizeEnabled
        >
            <ScrollView height="100%" width="100%">
                <SalesOrderDataEntryList
                    pickerMode
                    pickerFilter={pickerFilter}
                    excludeRowId={originalHeaderRowId}
                    onRecordSelected={handleListRecordSelected}
                    onPickerClose={closeListPopup}
                />
            </ScrollView>
        </Popup>
        </>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(SplitOrderPopup);
